import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  UsersIcon,
  CheckIcon,
  BellIcon,
} from '@heroicons/react/24/outline';

export default function NotificationsModal({ admin, onClose, onSave }) {
  const [settings, setSettings] = useState(null);
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer la structure des notifications
      const structureRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/notification-settings/structure`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStructure(structureRes.data);

      // Récupérer les paramètres actuels de l'admin
      try {
        const settingsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/notification-settings/${admin._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSettings(settingsRes.data);
      } catch (error) {
        // Si pas de paramètres, créer une structure vide
        const emptySettings = {};
        Object.keys(structureRes.data).forEach(category => {
          emptySettings[category] = {};
          structureRes.data[category].notifications.forEach(notif => {
            emptySettings[category][notif.key] = false;
          });
        });
        setSettings(emptySettings);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      alert('Erreur lors du chargement des paramètres de notification');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotification = (category, notificationKey) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [notificationKey]: !prev[category][notificationKey],
      },
    }));
  };

  const handleToggleCategory = (category) => {
    const allEnabled = structure[category].notifications.every(
      notif => settings[category][notif.key]
    );
    
    const newCategorySettings = {};
    structure[category].notifications.forEach(notif => {
      newCategorySettings[notif.key] = !allEnabled;
    });

    setSettings(prev => ({
      ...prev,
      [category]: newCategorySettings,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/notification-settings/${admin._id}`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Paramètres de notification mis à jour avec succès');
      onSave();
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde des paramètres de notification');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryIcon = (iconName) => {
    const icons = {
      UsersIcon,
      BellIcon,
    };
    return icons[iconName] || BellIcon;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <BellIcon className="h-6 w-6 mr-2 text-honey-500" />
              Paramètres de notification
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {admin.prenom} {admin.nom} - {admin.email}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Groupements : {admin.organismes?.join(', ') || admin.organisme || 'Non défini'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 p-4 bg-honey-50 border border-honey-200 rounded-lg">
            <p className="text-sm text-honey-800">
              <strong>Note :</strong> Les notifications seront envoyées uniquement pour les groupements auxquels cet administrateur est rattaché ({admin.organismes?.join(', ') || admin.organisme || 'aucun'}).
            </p>
          </div>

          <div className="space-y-6">
            {structure && settings && Object.keys(structure).map(categoryKey => {
              const category = structure[categoryKey];
              const Icon = getCategoryIcon(category.icon);
              const allEnabled = category.notifications.every(
                notif => settings[categoryKey]?.[notif.key]
              );
              const someEnabled = category.notifications.some(
                notif => settings[categoryKey]?.[notif.key]
              );

              return (
                <div key={categoryKey} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5 text-gray-600" />
                      <h3 className="font-medium text-gray-900">{category.label}</h3>
                    </div>
                    <button
                      onClick={() => handleToggleCategory(categoryKey)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        allEnabled
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : someEnabled
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {allEnabled ? 'Tout désactiver' : 'Tout activer'}
                    </button>
                  </div>

                  {/* Notifications List */}
                  <div className="divide-y divide-gray-200">
                    {category.notifications.map(notification => {
                      const isEnabled = settings[categoryKey]?.[notification.key];
                      
                      return (
                        <div
                          key={notification.key}
                          className="px-4 py-3 hover:bg-gray-50 transition-colors"
                        >
                          <label className="flex items-start cursor-pointer">
                            <div className="flex items-center h-5">
                              <input
                                type="checkbox"
                                checked={isEnabled || false}
                                onChange={() => handleToggleNotification(categoryKey, notification.key)}
                                className="h-4 w-4 text-honey-600 border-gray-300 rounded focus:ring-honey-500"
                              />
                            </div>
                            <div className="ml-3 flex-1">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900">
                                  {notification.label}
                                </span>
                                {isEnabled && (
                                  <CheckIcon className="h-4 w-4 text-green-500 ml-2" />
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                {notification.description}
                              </p>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-honey-600 rounded-md hover:bg-honey-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
